import React, { useCallback, useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { TextField, Tooltip, IconButton, Typography } from '@rodeo-dev/legos'
import * as Wrappers from './Wrappers.jsx'
import {
  incrementProjectNumber,
  isInArrayByKey,
  getErrorMessages
} from './misc'
import IntlUtil from '../../../lib/intlutil.jsx'

const ProjectNumberSuggestion = ({
  handleClickSuggestion,
  nextProjectNumberSuggestion
}) => {
  const Wrap = styled.div`
    display: flex;
    align-self: start;
    padding-top: ${({ theme: { typography } }) => typography.pxToRem(2)};
    padding-left: ${({ theme: { typography } }) => typography.pxToRem(8)};
  `
  const Suggestion = styled(Typography)`
    padding: ${({ theme: { typography } }) => typography.pxToRem(5)};
  `

  return (
    <Wrap>
      <Typography variant='body1'>
        {IntlUtil.convertMessage('project.create.lastUsedProjectNumber')}{' '}
      </Typography>
      <Suggestion variant='button'>{nextProjectNumberSuggestion}</Suggestion>
      <Tooltip
        placement='bottom'
        variant='dark'
        title={IntlUtil.convertMessage(
          'project.create.lastUsedProjectNumberCopy'
        )}
      >
        <IconButton onClick={handleClickSuggestion} size='small' icon='copy' />
      </Tooltip>
    </Wrap>
  )
}

ProjectNumberSuggestion.propTypes = {
  handleClickSuggestion: PropTypes.func.isRequired,
  nextProjectNumberSuggestion: PropTypes.string.isRequired
}

const ProjectNumberSuggestionMemo = React.memo(ProjectNumberSuggestion)

export const ProjectForm = ({
  isNumberSupported = false,
  isNumberAutoGenerated = false,
  nextProjectNumber = '',
  nextProjectNumberSuggestion = '',
  projectList = [],
  onTitleChange = () => {},
  onNumberChange = () => {},
  errors: receivedErrors
}) => {
  const [errors, setErrors] = useState({})

  const errorMessages = useMemo(() => getErrorMessages(), [getErrorMessages])

  // title controls
  const [title, setTitle] = useState('')
  const handleTitleChange = useCallback(
    (e) => {
      setErrors({ ...errors, title: '' })
      setTitle(e.target.value)
    },
    [errors]
  )

  // project number controls
  const [projectNumber, setProjectNumber] = useState(
    isNumberSupported && isNumberAutoGenerated ? nextProjectNumber : ''
  )

  useEffect(() => {
    setErrors({ ...errors, ...receivedErrors })
  }, [receivedErrors])

  useEffect(() => {
    onTitleChange(title)
  }, [title])

  useEffect(() => {
    onNumberChange(projectNumber)
  }, [projectNumber])

  const handleProjectNumberChange = useCallback(
    (e) => {
      setErrors({
        ...errors,
        projectNumber: ''
      })
      setProjectNumber(e.target.value)
    },
    [errors]
  )

  const handleClickSuggestion = useCallback(() => {
    setErrors({
      ...errors,
      projectNumber: ''
    })
    try {
      setProjectNumber(incrementProjectNumber(nextProjectNumberSuggestion))
    } catch (err) {
      setErrors({ ...errors, projectNumber: errorMessages.mandatory })
    }
  }, [errors])

  const validateDuplicate = useCallback(
    () =>
      setErrors({
        ...errors,
        title:
          isInArrayByKey(projectList, 'title', title) &&
          errorMessages.existingProject
      }),
    [errors, projectList, title]
  )

  return (
    <>
      <Wrappers.FormControl fullWidth>
        <TextField
          label={IntlUtil.convertMessage('project.list.orderByProjectName')}
          id='title'
          required
          onChange={handleTitleChange}
          value={title}
          error={!!errors.title}
          onBlur={validateDuplicate}
          helperText={errors.title}
        />
      </Wrappers.FormControl>
      {isNumberSupported && (
        <Wrappers.FormControl fullWidth>
          <TextField
            label={IntlUtil.convertMessage('project.list.orderByProjectNumber')}
            id='projectNumber'
            required
            InputProps={{
              disabled: isNumberAutoGenerated
            }}
            onChange={handleProjectNumberChange}
            value={projectNumber}
            error={!!errors.projectNumber}
            helperText={errors.projectNumber}
          />
          {nextProjectNumberSuggestion && (
            <ProjectNumberSuggestionMemo
              nextProjectNumberSuggestion={nextProjectNumberSuggestion}
              handleClickSuggestion={handleClickSuggestion}
            />
          )}
        </Wrappers.FormControl>
      )}
    </>
  )
}
